import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Zap, LogOut, User, Shield, Bell, Target, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/live", label: "Live Streams" },
  { to: "/standings", label: "Standings" },
  { to: "/schedule", label: "Schedule" },
  { to: "/match", label: "Predict" },
  { to: "/results", label: "Results" },
  { to: "/bracket", label: "Bracket" },
  { to: "/trophies", label: "Trophies" },
  { to: "/players", label: "Players" },
  { to: "/teams", label: "Teams" },
  { to: "/transfers", label: "Transfers" },
  { to: "/free-agents", label: "Free Agents" },
  { to: "/recruitment", label: "Recruit" },
  { to: "/streamers", label: "Streamers" },
  { to: "/streams", label: "Streams" },
  { to: "/vault", label: "Vault" },
  { to: "/rulebook", label: "Rulebook" },
];

const AUTHED_NAV = [{ to: "/my-team", label: "My Team" }, { to: "/rep-dashboard", label: "Dashboard" }, { to: "/reps", label: "Reps" }];
const CASTER_NAV = [{ to: "/streamers/me/cast-calendar", label: "Cast Calendar" }];
const OFFICIAL_NAV = [{ to: "/officials", label: "Officials" }];
const ADMIN_NAV = [{ to: "/admin", label: "Admin" }];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isAuthed = user && user !== false && typeof user === "object";
  const isAdmin = isAuthed && user.role === "admin";
  const isOfficial = isAuthed && (user.role === "official" || user.role === "admin");
  const [alerts, setAlerts] = useState({ alerts: [], unread: 0 });
  const [isVerifiedCaster, setIsVerifiedCaster] = useState(false);
  const [pendingProposals, setPendingProposals] = useState(0);
  const [pendingPredictions, setPendingPredictions] = useState(0);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem("predict-nudge-dismissed") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    if (!isAuthed) { setIsVerifiedCaster(false); setPendingProposals(0); setPendingPredictions(0); return; }
    api.get("/me/streamer").then((r) => setIsVerifiedCaster(!!(r.data && r.data.verified))).catch(() => setIsVerifiedCaster(false));
    const loadProposals = () =>
      api.get("/me/match-proposals").then((r) => setPendingProposals((r.data?.received || []).length)).catch(() => {});
    const loadPredictions = () =>
      api.get("/me/predictions/pending").then((r) => setPendingPredictions(r.data?.count || 0)).catch(() => {});
    loadProposals();
    loadPredictions();
    const t1 = setInterval(loadProposals, 20000);
    const t2 = setInterval(loadPredictions, 60000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [isAuthed, user?.email]);

  useEffect(() => {
    if (!isAuthed) return;
    const load = () => api.get("/me/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [isAuthed]);

  const markAllRead = async () => {
    try {
      await api.post("/alerts/read-all");
      setAlerts((a) => ({ ...a, alerts: a.alerts.map((x) => ({ ...x, read: true })), unread: 0 }));
    } catch {}
  };

  // Iter49 · OBS overlay routes must render fullbleed with no chrome so OBS
  // composites cleanly over the video source. Bypass the entire layout.
  if (location.pathname.startsWith("/overlay/")) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-[#05050A] text-white font-body">
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-[#232330]"
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 min-h-16 flex items-center justify-between gap-4 py-2">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-home">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_esports-legacy/artifacts/9vv3bfrj_LEGACY_HIL_logo.png"
              alt="LegacyHLL"
              className="w-20 h-20 lg:w-24 lg:h-24 object-contain group-hover:scale-105 transition-transform drop-shadow-[0_0_22px_rgba(244,208,63,0.45)]"
            />
            <span className="font-heading font-black tracking-tighter text-3xl lg:text-4xl uppercase hidden sm:inline">LegacyHLL</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5 flex-wrap justify-end flex-1 min-w-0">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-cyan-400 border-b-2 border-cyan-400" : "text-gray-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {isAuthed && AUTHED_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors relative ${
                    isActive ? "text-[#FF007F] border-b-2 border-[#FF007F]" : "text-gray-400 hover:text-white"
                  }`
                }
              >
                {n.label}
                {n.to === "/reps" && pendingProposals > 0 && (
                  <span
                    data-testid="nav-reps-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-mono font-bold flex items-center justify-center rounded-full px-1 animate-pulse"
                  >
                    {pendingProposals}
                  </span>
                )}
              </NavLink>
            ))}
            {isVerifiedCaster && CASTER_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-[#00FFB2] border-b-2 border-[#00FFB2]" : "text-[#00FFB2]/70 hover:text-[#00FFB2]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {isOfficial && !isAdmin && OFFICIAL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-[#B084FF] border-b-2 border-[#B084FF]" : "text-[#B084FF]/70 hover:text-[#B084FF]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {isAdmin && OFFICIAL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-[#B084FF] border-b-2 border-[#B084FF]" : "text-[#B084FF]/70 hover:text-[#B084FF]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {isAdmin && ADMIN_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-[#FF007F] border-b-2 border-[#FF007F]" : "text-[#FF007F]/70 hover:text-[#FF007F]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {isAuthed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="alerts-bell"
                    className="relative p-2 border border-[#232330] hover:border-cyan-400 transition-colors"
                    aria-label={`${alerts.unread} unread alerts`}
                  >
                    <Bell className={`w-4 h-4 ${alerts.unread ? "text-[#FF007F]" : "text-gray-400"}`} />
                    {alerts.unread > 0 && (
                      <span
                        data-testid="alerts-unread-count"
                        className="absolute -top-1.5 -right-1.5 bg-[#FF007F] text-white text-[9px] font-mono font-bold px-1.5 py-0.5 leading-none"
                      >
                        {alerts.unread > 9 ? "9+" : alerts.unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[#101015] border-[#232330] text-white rounded-none w-80 max-h-[70vh] overflow-y-auto"
                  data-testid="alerts-menu"
                >
                  <div className="px-3 py-2 border-b border-[#232330] flex items-center justify-between">
                    <div className="font-heading font-black uppercase tracking-tight text-xs">Alerts</div>
                    {alerts.unread > 0 && (
                      <button
                        onClick={markAllRead}
                        data-testid="alerts-mark-all"
                        className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {alerts.alerts.length === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-gray-500 font-mono">No alerts yet.</div>
                  )}
                  {alerts.alerts.map((a) => (
                    <DropdownMenuItem
                      key={a.id}
                      onSelect={() => nav(a.type === "round_published" ? "/reps" : "/free-agents")}
                      data-testid={`alert-${a.id}`}
                      className={`flex flex-col items-start gap-1 rounded-none border-b border-[#232330]/60 last:border-none cursor-pointer px-3 py-3 ${
                        a.read ? "opacity-60" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        {!a.read && <span className="w-1.5 h-1.5 bg-[#FF007F] rounded-full" />}
                        <span className="text-[10px] font-mono tracking-widest text-cyan-400 uppercase">
                          {a.type === "free_agent_match"
                            ? "Free Agent Match"
                            : a.type === "round_published"
                              ? `Round ${a.round_number} · Fixture`
                              : a.type}
                        </span>
                        <span className="ml-auto text-[9px] font-mono text-gray-500">
                          {new Date(a.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {a.type === "round_published" ? (
                        <>
                          <div className="text-sm">
                            <span className="font-bold">{a.team_tag}</span>
                            <span className="text-gray-500"> vs </span>
                            <span className="font-bold">{a.opponent_tag}</span>
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono">
                            Agree kickoff · weekend of {new Date(a.weekend_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm">
                            <span className="font-bold">{a.free_agent_handle}</span>
                            <span className="text-gray-500"> · {a.role} · {a.game}</span>
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono">Match for {a.team_name}</div>
                        </>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isAuthed ? (
              <>
                <button
                  onClick={() => nav("/dashboard")}
                  data-testid="nav-dashboard"
                  className="hidden md:flex items-center gap-2 px-3 py-2 border border-[#232330] hover:border-cyan-400 transition-colors text-xs uppercase tracking-widest"
                >
                  {isAdmin ? <Shield className="w-3.5 h-3.5 text-[#FF007F]" /> : <User className="w-3.5 h-3.5" />}
                  {isAdmin ? "Admin" : "Member"}
                </button>
                <button
                  onClick={() => nav("/profile")}
                  data-testid="nav-profile"
                  className="hidden md:flex items-center gap-2 px-3 py-2 border border-[#232330] hover:border-[#F4D03F] transition-colors text-xs uppercase tracking-widest"
                  title="Profile & Email settings"
                >
                  <User className="w-3.5 h-3.5" /> Profile
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="logout-button"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="text-gray-400 hover:text-[#FF007F] hover:bg-transparent"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" size="sm" className="text-xs uppercase tracking-widest">Log in</Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button size="sm" className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-xs rounded-none">
                    Join
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="lg:hidden border-t border-[#232330] overflow-x-auto">
          <div className="flex px-4 gap-4 whitespace-nowrap">
            {[...NAV, ...(isAuthed ? AUTHED_NAV : []), ...(isVerifiedCaster ? CASTER_NAV : []), ...(isOfficial ? OFFICIAL_NAV : []), ...(isAdmin ? ADMIN_NAV : [])].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `py-3 text-[10px] font-bold tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-cyan-400" : "text-gray-500"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>
      {isAuthed && pendingPredictions > 0 && !nudgeDismissed && (
        <div
          data-testid="predict-nudge"
          className="border-b border-[#F4D03F]/40 bg-gradient-to-r from-[#F4D03F]/10 via-[#F4D03F]/5 to-transparent"
        >
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center gap-3">
            <Target className="w-4 h-4 text-[#F4D03F] shrink-0 animate-pulse" />
            <div className="text-[11px] font-mono uppercase tracking-widest text-gray-300 flex-1 min-w-0">
              You haven't picked <span className="text-[#F4D03F] font-bold">{pendingPredictions}</span> game{pendingPredictions === 1 ? "" : "s"} yet this week —
            </div>
            <Link
              to="/match"
              data-testid="predict-nudge-cta"
              className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#F4D03F] hover:bg-yellow-300 px-3 py-1 font-bold shrink-0"
            >
              Lock picks
            </Link>
            <button
              onClick={() => {
                setNudgeDismissed(true);
                try { sessionStorage.setItem("predict-nudge-dismissed", "1"); } catch {}
              }}
              data-testid="predict-nudge-dismiss"
              className="text-gray-500 hover:text-white shrink-0"
              aria-label="Dismiss predict nudge"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12">
        {children}
      </main>
      <footer className="border-t border-[#232330] mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 text-xs text-gray-500 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-mono tracking-widest">// LEGACY LEAGUE · CIRCUIT 2026</div>
          <div className="tracking-widest uppercase">Broadcast · Compete · Ascend</div>
        </div>
      </footer>
    </div>
  );
}
